import { normalizeSymbolCode } from '@formosa/shared/utils/marketThumb';

const DEFAULT_LEVERAGE_PRESETS = [1, 5, 10, 20, 50, 100, 125];

function parseLeverageRange(range) {
  if (!range) {
    return { minLeverage: 1, maxLeverage: 125 };
  }
  const parts = String(range).split(',');
  if (parts.length < 2) {
    return { minLeverage: 1, maxLeverage: 125 };
  }
  const min = Number(parts[0].trim());
  const max = Number(parts[1].trim());
  return {
    minLeverage: Number.isNaN(min) ? 1 : min,
    maxLeverage: Number.isNaN(max) ? 125 : max,
  };
}

export function parsePerpetualPlayConfig(playConfig) {
  if (!playConfig) {
    return {
      defaultLeverage: 20,
      minLeverage: 1,
      maxLeverage: 125,
      stakingTimeDays: 0,
    };
  }
  try {
    const json = typeof playConfig === 'string' ? JSON.parse(playConfig) : playConfig;
    const range = parseLeverageRange(json.leverageRange);
    return {
      defaultLeverage: Number(json.defaultLeverage) || 20,
      minLeverage: range.minLeverage,
      maxLeverage: Number(json.maxLeverage) || range.maxLeverage,
      stakingTimeDays: Number(json.stakingTime) || 0,
    };
  } catch {
    return {
      defaultLeverage: 20,
      minLeverage: 1,
      maxLeverage: 125,
      stakingTimeDays: 0,
    };
  }
}

export function listLeverageOptions(playConfig) {
  const cfg = parsePerpetualPlayConfig(playConfig);
  const presets = DEFAULT_LEVERAGE_PRESETS.filter(
    (value) => value >= cfg.minLeverage && value <= cfg.maxLeverage
  );
  if (!presets.includes(cfg.defaultLeverage)) {
    presets.push(cfg.defaultLeverage);
  }
  return [...new Set(presets)].sort((a, b) => a - b);
}

export function formatStakingLabel(stakingTimeDays) {
  const days = Number(stakingTimeDays);
  if (!days || Number.isNaN(days) || days <= 0) {
    return '不限制';
  }
  return `${days} 天`;
}

export function calcPerpetualUnrealizedPnl(orderType, orderPrice, currentPrice, betAmount, leverage) {
  const open = Number(orderPrice);
  const current = Number(currentPrice);
  const bet = Number(betAmount);
  const lev = Number(leverage);
  if ([open, current, bet, lev].some((value) => Number.isNaN(value) || value <= 0)) {
    return 0;
  }
  let rate = (current - open) / open;
  if (orderType === 'SHORT') {
    rate = -rate;
  }
  return rate * bet * lev;
}

export function calcPerpetualRoi(unrealizedPnl, margin) {
  const m = Number(margin);
  if (!m || Number.isNaN(m)) {
    return 0;
  }
  return (Number(unrealizedPnl) / m) * 100;
}

export function isPerpetualPending(order) {
  return order?.status === 'PENDING';
}

export function isPerpetualOpen(order) {
  return order?.status === 'OPEN';
}

export function pickPrimaryPerpetualProduct(products) {
  if (!Array.isArray(products) || products.length === 0) {
    return null;
  }
  return [...products].sort((a, b) => (a.sort ?? 9999) - (b.sort ?? 9999))[0];
}

export function pickPerpetualProductBySymbol(products, marketSymbolCode) {
  if (!marketSymbolCode) {
    return pickPrimaryPerpetualProduct(products);
  }
  const normalized = normalizeSymbolCode(marketSymbolCode);
  const matched = products.filter(
    (item) => normalizeSymbolCode(item.marketSymbolCode) === normalized
  );
  if (matched.length === 0) {
    return pickPrimaryPerpetualProduct(products);
  }
  return [...matched].sort((a, b) => (a.sort ?? 9999) - (b.sort ?? 9999))[0];
}
